import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Events,
  NewReport,
  Report,
  ReportRepositoryService,
  ReportUnlockedPayload,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientReportsStore — saved clinical reports for the current patient.
 *
 * The 90% flow: a draft is created via createReport()/duplicate(), the
 * draft editor (CreateReportComponent) loads it via load() and persists
 * incremental edits via update(); the final save sets `locked: true` and
 * we reload the list. There is no push event for the reports list, so
 * cross-client refresh happens via reload() at known points.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientReportsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(ReportRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<Report[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  private currentChannel: string | null = null;
  private readonly unlockedEventName = Events.ReportGenerationUnlocked;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.unlockedEventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        // Report-unlocked fires after a rollback-to-draft; clear the locked
        // flag in place for immediate feedback, then reload so any derived
        // views (filters, sorts, draft listing seeds) see the fresh state.
        this.websocket.subscribe(channel, this.unlockedEventName, (data: ReportUnlockedPayload) => {
          this.applyUnlocked(data?.reportId);
          this.reload();
        });
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Create a new report draft from a NewReport DTO (template + patient ids).
   * Returns the new report id; caller registers via DraftsService for
   * editing.
   */
  createReport(report: NewReport): Observable<number> {
    return this.repo.createReport(report);
  }

  /**
   * Duplicate an existing report. Returns the new report id; caller
   * registers via DraftsService.
   */
  duplicate(reportId: number): Observable<number> {
    return this.repo.duplicateReport(reportId);
  }

  /**
   * Update a report (used by the draft editor for incremental autosave
   * and the explicit lock on commit). Reloads the list when locked so
   * the new entry appears.
   */
  update(report: Report): Observable<boolean> {
    return this.repo.updateReport(report).pipe(
      tap({
        next: success => {
          if (success && report.locked) this.reload();
        }
      })
    );
  }

  /**
   * Rollback a locked report to draft. Optimistically clears the locked
   * flag in the store on success; the matching Pusher event will arrive
   * shortly after and is a no-op for this client (idempotent).
   */
  unlock(reportId: number): Observable<boolean> {
    return this.repo.unlockReport(reportId).pipe(
      tap({
        next: success => {
          if (success) {
            this.applyUnlocked(reportId);
            this.reload();
          }
        }
      })
    );
  }

  delete(reportId: number): Observable<boolean> {
    return this.repo.deleteReport(reportId).pipe(
      tap({
        next: success => {
          if (success) {
            this._items.update(items => items.filter(r => r.id !== reportId));
          }
        }
      })
    );
  }

  private applyUnlocked(reportId: number | undefined): void {
    if (reportId == null) return;
    this._items.update(items =>
      items.map(r => (r.id === reportId ? {...r, locked: false, lockedAt: null} : r))
    );
  }

  /**
   * Fetch a single report by id (one-off lookup for the draft editor;
   * does not affect the list signal).
   */
  load(reportId: number): Observable<Report> {
    return this.repo.getReport(reportId);
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientReports(patientId).subscribe({
      next: reports => {
        this._items.set(reports);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient reports');
        
      }
    });
  }
}
